import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ToastService } from '../services/toast.service';

/**
 * Add this header to a request to suppress the automatic error toast.
 * Useful when a component wants to handle errors itself.
 *
 * Example:
 *   this.http.get('/api/resource', { headers: { 'X-Skip-Error-Toast': 'true' } })
 */
export const SKIP_ERROR_TOAST_HEADER = 'X-Skip-Error-Toast';

/** Auth endpoints — components handle their own error feedback here. */
const AUTH_URL_PATTERNS = [
  /\/auth\/login/,
  /\/auth\/register/,
  /\/auth\/sign-up/,
  /\/auth\/signup/,
  /\/auth\/firebase/,
  /\/auth\/google/,
  /\/auth\/apple/,
  /\/auth\/forgot-password/,
  /\/auth\/reset-password/,
  /\/auth\/validate-reset-token/,
  /\/auth\/verify-email/,
  /\/auth\/refresh/,
  /\/auth\/logout/,
];

function isAuthUrl(url: string): boolean {
  return AUTH_URL_PATTERNS.some(p => p.test(url));
}

function extractServerMessage(error: HttpErrorResponse): string | null {
  const body = error.error;
  if (!body) return null;
  if (typeof body === 'string') return body.length < 200 ? body : null;
  return body.message || body.error || body.detail || null;
}

function resolveUserMessage(error: HttpErrorResponse): string {
  const serverMsg = extractServerMessage(error);

  switch (error.status) {
    case 0:
      return 'No internet connection. Check your network and try again.';
    case 400:
      return serverMsg || 'The request was invalid. Please check your input.';
    case 403:
      return 'You do not have permission to do that.';
    case 404:
      return serverMsg || 'The requested resource was not found.';
    case 408:
      return 'The request timed out. Please try again.';
    case 409:
      return serverMsg || 'A conflict occurred. The action could not be completed.';
    case 422:
      return serverMsg || 'The data you submitted could not be processed. Please review and try again.';
    case 429:
      return 'Too many requests. Please wait a moment before trying again.';
    case 500:
      return 'Something went wrong on our side. Please try again in a moment.';
    case 502:
    case 503:
    case 504:
      return 'The server is temporarily unavailable. Please try again shortly.';
    default:
      if (error.status >= 500) {
        return 'An unexpected server error occurred. Please try again.';
      }
      return serverMsg || 'An unexpected error occurred. Please try again.';
  }
}

export const HttpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const toast = inject(ToastService);

  // Strip the opt-out header before forwarding (keep it internal)
  const skipToast = req.headers.has(SKIP_ERROR_TOAST_HEADER);
  const cleanReq = skipToast
    ? req.clone({ headers: req.headers.delete(SKIP_ERROR_TOAST_HEADER) })
    : req;

  return next(cleanReq).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        // Non-HTTP errors (e.g. timeout Error thrown by JWT interceptor)
        if (!skipToast) {
          const msg = error instanceof Error ? error.message : 'An unexpected error occurred.';
          toast.showError(msg);
        }
        return throwError(() => error);
      }

      // 401 is handled by auth.interceptor (token refresh) — skip toast
      if (error.status === 401) {
        return throwError(() => error);
      }

      // Auth endpoints handle their own feedback
      if (isAuthUrl(error.url ?? '')) {
        return throwError(() => error);
      }

      if (!skipToast) {
        const message = resolveUserMessage(error);

        if (error.status === 0 || error.status >= 500) {
          toast.showError(message, { duration: 5000 });
        } else if (error.status === 422 || error.status === 400) {
          toast.showWarning(message, { duration: 4500 });
        } else {
          toast.showError(message, { duration: 4000 });
        }
      }

      return throwError(() => error);
    })
  );
};
